import {
  PLAN_TIER_LABEL,
  lowestTierWithFeature,
  planHasFeature,
  tiersWithFeature,
  type PlanFeature,
  type PlanTier,
  type SalonBillingStatus,
} from '@repo/shared/billing';

/**
 * What each Growth+ feature is called and does, in one line, for locked states and upgrade prompts.
 * Plan names and which tiers include a feature come from the shared plan catalog, never from this file.
 */
export const PLAN_FEATURE_COPY: Record<PlanFeature, { name: string; value: string }> = {
  BULK_SMS: { name: 'Bulk texting', value: 'Send a promo text to many clients at once, in safe daily waves.' },
  REFERRALS: { name: 'Friends of your clients', value: 'Clients share a link, friends get a first-visit offer, and both get rewarded.' },
  PRIZES: { name: 'Prizes', value: 'Run a monthly giveaway that brings winners back through your door.' },
  LOYALTY: { name: 'Rewards card', value: 'Clients tap their phone at the counter to collect stamps and earn a reward.' },
  CREATORS: { name: 'Creators', value: 'Give each local creator a tracked link and see who they bring in.' },
  GOOGLE_RANKING: { name: 'Google Ranking', value: 'See where you show up on Google Maps and the fixes that move you up.' },
  AI_REVIEW_REPLIES: { name: 'AI review replies', value: 'Write a warm reply to every Google review in one click.' },
  AI_GBP_POSTS: { name: 'Google posts', value: 'Keep your Google profile fresh with posts written and scheduled for you.' },
  LANDING_AI: { name: 'AI page writer', value: 'Turn a promo or a flyer into a ready-to-share page in seconds.' },
  COMMENT_TO_DM: { name: 'Comment → DM', value: 'When someone comments a keyword, they get your offer in their DMs.' },
  AI_PHONE: { name: 'AI phone assistant', value: 'Answers the calls you miss and books the appointment.' },
  PRIORITY_SUPPORT: { name: 'Priority support', value: 'Your questions jump to the front of the line.' },
  AGENCY_CAMPAIGN_SETUP: { name: 'Campaign setup', value: 'Your agency sets up your campaigns for you.' },
};

/** "Growth", the plan an upgrade prompt names. */
export function planFeatureRequiredTierLabel(feature: PlanFeature): string {
  return PLAN_TIER_LABEL[lowestTierWithFeature(feature)];
}

/** "Growth · Pro": every plan that includes the feature. */
export function planFeatureIncludedInLabel(feature: PlanFeature): string {
  return tiersWithFeature(feature)
    .map((tier) => PLAN_TIER_LABEL[tier])
    .join(' · ');
}

/** Toast and tooltip sentence for an action the plan does not include. */
export function planUpgradeMessage(feature: PlanFeature): string {
  return `${PLAN_FEATURE_COPY[feature].name}: part of the ${planFeatureRequiredTierLabel(feature)} plan. Ask your agency about upgrading.`;
}

/**
 * The salon picked a plan that includes `feature`, but unpaid / canceled billing drops it to Essentials: the fix is
 * billing, not an upgrade. Returns that plan's name ("Growth"), or null when an upgrade is what is needed.
 */
export function planFeaturePausedByBilling(
  plan: { tier: PlanTier; status: SalonBillingStatus } | null,
  feature: PlanFeature
): string | null {
  if (!plan || (plan.status !== 'UNPAID' && plan.status !== 'CANCELED')) return null;
  return planHasFeature(plan.tier, feature) ? PLAN_TIER_LABEL[plan.tier] : null;
}
